import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageSEO } from '@/hooks/usePageSEO';

const advisorySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Board & Advisory — Jeff Dumé',
  description: 'Jeff Dumé works with founders, leadership teams, and organizations navigating growth, workforce transformation, and operational change through workforce intelligence, operational systems, and responsible AI.',
  about: {
    '@type': 'Person',
    name: 'Jeff Dumé',
    jobTitle: 'Board & Advisory Consultant',
    knowsAbout: ['Workforce Intelligence', 'Operational Systems', 'Responsible AI', 'Leadership and Organizational Growth', 'Strategic Partnerships'],
  },
};

const focusAreas = [
  {
    title: 'Workforce Intelligence',
    desc: 'How structured evaluation reduces hiring risk and builds long-term workforce performance — moving beyond filling seats to building organizational capability.',
    icon: 'ri-brain-line',
  },
  {
    title: 'Operational Systems',
    desc: 'What it takes to create visibility, accountability, and execution across the critical workflows that determine whether strategy translates into results.',
    icon: 'ri-settings-3-line',
  },
  {
    title: 'Infrastructure Innovation',
    desc: 'Where workforce strategy, operational discipline, and emerging technology converge to strengthen the industries that build modern communities.',
    icon: 'ri-building-4-line',
  },
  {
    title: 'Responsible AI',
    desc: 'Practical frameworks for adopting AI without surrendering human judgment, decision ownership, or organizational standards.',
    icon: 'ri-scales-3-line',
  },
  {
    title: 'Leadership and Organizational Growth',
    desc: 'The intersection of people, systems, and technology — aligned under one accountable vision for sustainable growth.',
    icon: 'ri-line-chart-line',
  },
  {
    title: 'Strategic Partnerships',
    desc: 'The connections and capabilities that give organizations leverage to scale without losing operational coherence.',
    icon: 'ri-organization-chart',
  },
];

const mainframeServices = [
  'Executive Search',
  'BuildFit AI Implementation',
  'Custom Software Development',
  'Workflow Automation',
  'AI Governance',
  'Strategic Advisory',
];

export default function BoardAdvisory() {
  usePageSEO({
    title: 'Jeff Dumé | Board & Advisory — Workforce Intelligence, Operational Systems & Responsible AI | Houston, TX',
    description: 'Jeff Dumé works with founders, leadership teams, and organizations navigating growth, workforce transformation, and operational change. Workforce intelligence, operational systems, responsible AI, and strategic partnerships.',
    keywords: 'Jeff Dumé board advisory, workforce intelligence consultant, operational systems advisor, responsible AI consultant, leadership growth advisor, Mainframe Solutions professional services, BuildFit AI implementation, Houston board advisory, strategic partnerships',
    canonical: '/board-advisory',
    ogTitle: 'Jeff Dumé | Board & Advisory — Helping Organizations Align Leadership, Systems, and Technology',
    ogDescription: 'Supporting organizations through workforce intelligence, operational systems, responsible AI, and strategic partnerships. Founder of BuildFit AI and Mainframe Solutions.',
    schema: advisorySchema,
    breadcrumbs: [{ name: 'Board & Advisory', item: '/board-advisory' }],
  });

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-20 sm:pt-24 md:pt-28 pb-14 md:pb-22">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-4 sm:mb-5">
              Board &amp; Advisory
            </p>
            <h1 className="text-white text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-6 sm:mb-8 font-display">
              Helping Organizations Align Leadership, Systems, and Technology
            </h1>
            <div className="space-y-5 text-white/60 text-sm md:text-base leading-relaxed max-w-3xl">
              <p>
                Jeff Dum&eacute; works with founders, leadership teams, and organizations navigating growth, workforce transformation, and operational change.
              </p>
              <p>
                His experience spans executive search, operational systems, responsible AI, and workforce strategy, allowing him to help organizations think through the decisions that shape long-term performance.
              </p>
              <p>
                Jeff&rsquo;s perspective is influenced by years spent supporting organizations operating in construction, infrastructure, engineering, healthcare, technology, and regulated environments where execution, accountability, and leadership decisions carry significant consequences.
              </p>
            </div>
          </div>
        </section>

        {/* Areas of Focus */}
        <section className="py-14 md:py-22 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 text-center">
              Focus
            </p>
            <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-10 sm:mb-12 text-center">
              Areas of Focus
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {focusAreas.map((area) => (
                <div
                  key={area.title}
                  className="bg-dark-card rounded-xl p-6 md:p-8 border border-white/5 hover:border-gold/20 transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-gold/10 group-hover:bg-gold/20 rounded-lg shrink-0 transition-colors">
                      <i className={`${area.icon} text-gold text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base mb-2">
                        {area.title}
                      </h3>
                      <p className="text-white/40 text-sm leading-relaxed">{area.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BuildFit AI */}
        <section className="py-16 md:py-24 lg:py-28 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <div className="bg-dark-card rounded-2xl p-8 md:p-12 lg:p-14 border border-white/5">
              <div className="text-center mb-8">
                <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-4">
                  BuildFit AI&trade;
                </p>
                <h2 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-4 font-display">
                  The Platform Behind the Mission
                </h2>
                <p className="text-white/50 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                  BuildFit AI helps organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through human-defined evaluation and AI-supported insights.
                </p>
              </div>

              <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8">
                <p className="text-white/80 font-medium text-center">
                  Human-designed evaluation frameworks. AI supports the process.
                </p>
                <p className="text-gold/70 text-sm text-center">
                  The person making the hire always owns the outcome.
                </p>
              </div>

              <div className="text-center">
                <a
                  href="https://buildfitai.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gold text-charcoal font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
                >
                  Learn More About BuildFit AI
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mainframe Solutions */}
        <section className="py-16 md:py-24 lg:py-28 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <div className="bg-dark-card rounded-2xl p-8 md:p-12 lg:p-14 border border-white/5">
              <div className="text-center mb-8">
                <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-4">
                  Mainframe Solutions
                </p>
                <h2 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-4 font-display">
                  Professional Services and Implementation
                </h2>
                <p className="text-white/50 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                  Mainframe Solutions serves as the professional services and implementation partner behind many of the ideas and frameworks Jeff discusses.
                </p>
              </div>

              <p className="text-white/40 text-sm mb-6 text-center">Through Mainframe Solutions, organizations can access:</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-8">
                {mainframeServices.map((service) => (
                  <div
                    key={service}
                    className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-3"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-gold shrink-0"></div>
                    <span className="text-white/70 text-sm">{service}</span>
                  </div>
                ))}
              </div>

              <p className="text-white/50 text-sm leading-relaxed max-w-2xl mx-auto text-center mb-8">
                Mainframe Solutions helps organizations align people, systems, and technology under one accountable partner.
              </p>

              <div className="text-center">
                <a
                  href="https://mainframesolutions.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gold text-charcoal font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
                >
                  Explore Mainframe Solutions
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom footer-decoration spacer */}
        <section className="py-14 md:py-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-8 h-[1px] bg-white/10"></span>
              <div className="w-2 h-2 rounded-full bg-gold/40"></div>
              <span className="w-8 h-[1px] bg-white/10"></span>
            </div>
            <p className="text-white/20 text-xs tracking-wide uppercase">
              Decisions that shape organizational performance
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}